import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useMediaDevices — lists camera/mic/speaker devices and tracks permission state.
 * Persists last-selected device IDs in localStorage.
 */
const LS_KEY = 'muzi_media_prefs';

export function useMediaDevices() {
  const [devices, setDevices] = useState({ cameras: [], mics: [], speakers: [] });
  const [permission, setPermission] = useState({ camera: 'unknown', mic: 'unknown' });
  const [selected, setSelected] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
  });

  const refresh = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const list = await navigator.mediaDevices.enumerateDevices();
    setDevices({
      cameras: list.filter((d) => d.kind === 'videoinput'),
      mics: list.filter((d) => d.kind === 'audioinput'),
      speakers: list.filter((d) => d.kind === 'audiooutput'),
    });
  }, []);

  useEffect(() => {
    refresh();
    navigator.mediaDevices?.addEventListener?.('devicechange', refresh);
    // Best-effort: query Permissions API if supported
    (async () => {
      try {
        if (navigator.permissions?.query) {
          const [cam, mic] = await Promise.all([
            navigator.permissions.query({ name: 'camera' }).catch(() => null),
            navigator.permissions.query({ name: 'microphone' }).catch(() => null),
          ]);
          setPermission({
            camera: cam?.state || 'unknown',
            mic: mic?.state || 'unknown',
          });
        }
      } catch (err) {
        console.warn('[useMediaDevices] permissions query failed:', err?.message || err);
      }
    })();
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', refresh);
  }, [refresh]);

  const select = useCallback((kind, deviceId) => {
    setSelected((prev) => {
      const next = { ...prev, [kind]: deviceId };
      try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch (err) {
        console.warn('[useMediaDevices] localStorage write failed:', err?.message || err);
      }
      return next;
    });
  }, []);

  return { devices, permission, selected, select, refresh, setPermission };
}
